import GoogleReCAPTCHA, { ReCAPTCHA } from 'react-google-recaptcha'
import React, { FC, ReactElement, RefObject, useEffect, useRef, useState } from 'react'
import { DarkModeType, useDarkMode } from '../../../../React/Common/Hooks/useDarkMode'
import { ReactState, ReCaptchaResponse, ReCaptchaResponseEnabled } from '../../../../React/Common/Types/Types'
import { getReCaptchaConfig } from '../../../../React/Common/API/ConfigAPI'
import { RESTAPIError } from '../../../../React/Common/API'
import { StatusCodes } from '../../../../NonReact/constants/StatusCodes'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'

export type ReCaptchaHandle = {
    config: Nullable<ReCaptchaResponse>
    ref: RefObject<ReCAPTCHA>
}

type Props = {
    setCaptchaHandle: (handle: ReCaptchaHandle) => void
    onChange: (recaptchaToken: OnlyNullable<string>) => void
}

export const RECAPTCHA_CAPTCHA_TYPE_INVISIBLE: string = 'Invisible'

export const ReCaptcha: FC<Props> = ({ onChange, setCaptchaHandle }: Props): ReactElement => {
    const { isDarkMode }: DarkModeType = useDarkMode()

    const ref: RefObject<ReCAPTCHA> = useRef<ReCAPTCHA>(null)
    const [recaptchaConfig, setRecaptchaConfig]: ReactState<Nullable<ReCaptchaResponse>> =
        useState<Nullable<ReCaptchaResponse>>()

    useEffect((): void => {
        getReCaptchaConfig()
            .then((response: Nullable<ReCaptchaResponseEnabled>): void =>
                setRecaptchaConfig({
                    enabled: true,
                    captchaType: response?.captchaType ?? EMPTY_STRING,
                    publicKey: response?.publicKey ?? EMPTY_STRING
                })
            )
            .catch((e: RESTAPIError): void => {
                if (e.HTTPStatus === StatusCodes.NOT_FOUND) {
                    setRecaptchaConfig({
                        enabled: false
                    })
                }
            })
    }, [])

    useEffect((): void => {
        setCaptchaHandle({
            config: recaptchaConfig,
            ref
        })
    }, [recaptchaConfig, ref.current])

    return recaptchaConfig && recaptchaConfig?.enabled ? (
        <GoogleReCAPTCHA
            className='fp-recaptcha'
            theme={isDarkMode ? 'dark' : 'light'}
            sitekey={recaptchaConfig?.publicKey ?? EMPTY_STRING}
            size={recaptchaConfig.captchaType === RECAPTCHA_CAPTCHA_TYPE_INVISIBLE ? 'invisible' : 'normal'}
            onChange={onChange}
            ref={ref}
        />
    ) : (
        <></>
    )
}
